import { Music, Save, Trash2, Upload } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Track } from "../../types";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface EditTrackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  track: Track;
  onSave: (updatedTrack: Partial<Track>) => void;
}

export const EditTrackDialog: React.FC<EditTrackDialogProps> = ({ isOpen, onClose, track, onSave }) => {
  const [formData, setFormData] = useState({
    title: track.title,
    artist: track.artist,
    albumArt: track.albumArt || "",
    genre: track.genre || "",
    year: track.year || "",
    album: track.album || "",
    trackNumber: track.trackNumber || "",
  });

  // Initialize imagePreview with track's albumArt
  const [imagePreview, setImagePreview] = useState<string | null>(track.albumArt);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (e.g., max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert("Image file is too large. Maximum size is 2MB.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
        setFormData((prev) => ({ ...prev, albumArt: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert empty strings to null/undefined for optional fields
    const cleanedData = {
      ...formData,
      albumArt: formData.albumArt || null,
      genre: formData.genre || undefined,
      year: formData.year || undefined,
      album: formData.album || undefined,
      trackNumber: formData.trackNumber || undefined,
    };
    onSave(cleanedData);
    onClose();
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, albumArt: "" }));
  };

  // Reset form when track changes
  useEffect(() => {
    setFormData({
      title: track.title,
      artist: track.artist,
      albumArt: track.albumArt || "",
      genre: track.genre || "",
      year: track.year || "",
      album: track.album || "",
      trackNumber: track.trackNumber || "",
    });
    setImagePreview(track.albumArt);
  }, [track]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Music className="w-6 h-6" />
            Edit Track
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Album Art Section */}
            <div className="space-y-4">
              <Label>Album Art</Label>
              <div className="relative group">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Album art preview"
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      onClick={removeImage}
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-square bg-zinc-800 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-700 transition"
                  >
                    <Upload className="w-8 h-8 mb-2 text-zinc-400" />
                    <span className="text-sm text-zinc-400">Upload Image</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              <div>
                <Label>Or use image URL</Label>
                <Input
                  type="url"
                  value={formData.albumArt}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, albumArt: e.target.value }));
                    setImagePreview(e.target.value);
                  }}
                  className="bg-zinc-800"
                  placeholder="https://example.com/album-art.jpg"
                />
              </div>
            </div>

            {/* Track Details Section */}
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="bg-zinc-800"
                  required
                />
              </div>

              <div>
                <Label>Artist</Label>
                <Input
                  type="text"
                  value={formData.artist}
                  onChange={(e) => setFormData((prev) => ({ ...prev, artist: e.target.value }))}
                  className="bg-zinc-800"
                  required
                />
              </div>

              <div>
                <Label>Album</Label>
                <Input
                  type="text"
                  value={formData.album}
                  onChange={(e) => setFormData((prev) => ({ ...prev, album: e.target.value }))}
                  className="bg-zinc-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Track Number</Label>
                  <Input
                    type="number"
                    value={formData.trackNumber}
                    onChange={(e) => setFormData((prev) => ({ ...prev, trackNumber: e.target.value }))}
                    className="bg-zinc-800"
                    min="1"
                  />
                </div>

                <div>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData((prev) => ({ ...prev, year: e.target.value }))}
                    className="bg-zinc-800"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>

              <div>
                <Label>Genre</Label>
                <Input
                  type="text"
                  value={formData.genre}
                  onChange={(e) => setFormData((prev) => ({ ...prev, genre: e.target.value }))}
                  className="bg-zinc-800"
                  placeholder="e.g., Rock, Pop, Jazz"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-yellow-500 text-white hover:bg-yellow-600">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
